import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { v4 as uuidv4 } from "uuid";

// Third party
import moment from "moment";
import clsx from "clsx";

// Redux
import { useSelector } from "react-redux";

// React Query
import { useQuery } from "@tanstack/react-query";

// API's
import { getStartDatePayPeriodForPayPlanAssignment } from "@/PayPlans/api/cycles/queries";

// MUI
import { Button, FormControlLabel, Menu, MenuItem, Typography, Box, FormControl, RadioGroup, Radio, Skeleton } from "@mui/material";

// Icons
import AssignIcon from "./AssignIcon";
import CalendarIcon from "@/assets/icons/svg/calendar.svg";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

const cycleDatesOptions = (props) => {
	const { cycles = [], dateSelected, notificationType, handleDateClick = () => {} } = props;
	if (cycles?.length) {
		return cycles.map((date, i) => (
			<MenuItem
				key={`cycle_dates_options_${date}_${i}`}
				disabled={!notificationType}
				onClick={() => handleDateClick?.(date)}
				className={clsx({
					"!text-primary": date === dateSelected,
					"!text-default": date !== dateSelected,
					"!p-0 !mt-[0px] !px-3 !py-1": true,
				})}
				value={date}
				selected={date === dateSelected}
			>
				<img src={String(CalendarIcon)} className="!w-[17.8x] !h-[20px] !mr-[6px]" />
				<Typography className="!font-sans !font-normal !text-[14.5px] !truncate">
					{moment(date).utc().format(DATE_FORMAT.monthAbbrDatePadded)}
				</Typography>
			</MenuItem>
		));
	}
	return null;
};
cycleDatesOptions.propTypes = {
	cycles: PropTypes.array,
	dateSelected: PropTypes.string,
	notificationType: PropTypes.bool,
	handleDateClick: PropTypes.func,
};

export default function AssignPayPlanButton(props) {
	const {
		data = null,
		selected = null,
		selectedDate,
		hireDate,
		disabled = false,
		updateUpcomingPayPlan = false,
		onDateChanged = () => {},
		updateSelectedPlan = () => {},
	} = props;

	const [anchorEl, setAnchorEl] = React.useState(null);
	const [dateSelected, setDateSelected] = React.useState(selectedDate || null);
	const [notificationType, setNotificationType] = React.useState(null);
	const [emailRadio, setEmailRadio] = React.useState(null);
	const isMenuOpen = Boolean(anchorEl);
	const location = useSelector((state) => state.location.location);
	const locationId = location?.locationId;

	useEffect(() => {
		if (disabled) {
			setAnchorEl(null);
		}
	}, [disabled]);

	const { data: cycles, status } = useQuery({
		queryKey: ["assignSelectedPlanButton", locationId, hireDate],
		queryFn: async () => {
			if (hireDate) {
				return [hireDate];
			}

			return getStartDatePayPeriodForPayPlanAssignment(locationId);
		},
		staleTime: Infinity,
		enabled: !!locationId && isMenuOpen,
	});

	const handleMenuOpen = (e) => {
		if (updateUpcomingPayPlan) {
			updateSelectedPlan?.({
				locationId: data?.locationId,
				packageId: data?.upcomingPayPlanAssignment.packageId,
				user: { _id: data?._id, locationId: props.data?.locationId, position: props.selected?.position },
				payPlan: { name: selected?.name, position: selected?.position },
			});
			return;
		}
		setAnchorEl(e.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
		setNotificationType(null);
	};

	const handleListClick = (e) => {
		switch (e.target.value) {
			case "addendum":
				setNotificationType("addendum");
				// TODO:
				break;
			case "newPayPlan":
				setNotificationType("newPayPlan");
				// TODO:
				break;
			default:
				break;
		}
	};

	const handleOnChangeEmail = (e) => {
		switch (e.target.value) {
			case "business":
				// TODO:
				setEmailRadio("business");
				break;
			case "personal":
				// TODO:
				setEmailRadio("personal");
				break;
			default:
				break;
		}
	};

	const handleDateClick = (date) => {
		const newDate = moment(date).utc().format(DATE_FORMAT.usDateSlash);
		onDateChanged?.(newDate);
		setDateSelected(newDate);
		handleMenuClose();
	};

	const radioOptionsSend = [
		{ value: "addendum", label: "Addendum" },
		{ value: "newPayPlan", label: "New Pay Plan" },
	];

	const radioOptionsEmail = [
		{ value: "business", label: "Business" },
		{ value: "personal", label: "Personal" },
	];

	return (
		<>
			<Button
				id="select-plan-dialog-assign-btn"
				className={clsx({
					"select-plan-dialog-assign-btn": true,
					"!w-[153px] !p-0": true,
				})}
				variant="contained"
				aria-controls="select-plan-dialog-assign-btn-menu"
				onClick={handleMenuOpen}
				disabled={disabled}
			>
				<Typography className="!font-sans !text-white !text-[14.5px] !font-normal !normal-case" whiteSpace={"nowrap"}>
					Assign Selected Plan
				</Typography>
				<AssignIcon />
			</Button>
			<Menu
				anchorEl={anchorEl}
				id="select-plan-dialog-assign-btn-menu"
				open={isMenuOpen}
				onClose={handleMenuClose}
				MenuListProps={{
					"aria-labelledby": "select-plan-dialog-assign-btn",
				}}
				className={clsx({
					"[&>.MuiPaper-root]:!w-[153px]": true,
					"[&>.MuiPaper-root]:!rounded-[5px]": true,
					"[&>.MuiPaper-root]:!pb-3": true,
					"[&>.MuiPaper-root>ul]:!p-0": true,
				})}
			>
				<Typography className="!font-sans text-[14.5px] !font-bold !text-default !normal-case !px-3 !pt-3">Send as</Typography>
				<Box ml={1} className="!m-0 !px-3">
					<FormControl>
						<RadioGroup
							value={notificationType}
							onChange={handleListClick}
							aria-labelledby="demo-radio-buttons-group-label"
							name="radio-buttons-group"
						>
							{radioOptionsSend.map((option, index) => (
								<FormControlLabel
									key={uuidv4()}
									className={clsx({
										"!-mb-[10px]": index === 0,
									})}
									value={option.value}
									control={
										<Radio className="!text-primary [&.Mui-checked]:!text-primary [&_.MuiSvgIcon-root]:!text-[16px]" />
									}
									label={
										<Typography className="!font-sans !text-[14.5px] !font-normal !text-default">
											{option.label}
										</Typography>
									}
								/>
							))}
						</RadioGroup>
					</FormControl>
				</Box>
				<Typography
					className={clsx({
						"!font-sans text-[14.5px] !font-bold !normal-case !px-3": true,
						"!text-default": false,
						"!text-disabled": true,
					})}
				>
					Email
				</Typography>
				<Box ml={1} className="!m-0 !px-3">
					<FormControl>
						<RadioGroup
							value={emailRadio}
							onChange={handleOnChangeEmail}
							aria-labelledby="demo-radio-buttons-group-label"
							name="radio-buttons-group"
						>
							{radioOptionsEmail.map((option, index) => (
								<FormControlLabel
									disabled
									key={uuidv4()}
									className={clsx({
										"!-mb-[10px]": index === 0,
									})}
									value={option.value}
									control={
										<Radio
											className={clsx({
												"[&.Mui-checked]:!text-primary [&_.MuiSvgIcon-root]:!text-[16px]": true,
												"!text-primary": false,
												"!text-disabled": true,
											})}
										/>
									}
									label={
										<Typography
											className={clsx({
												"!font-sans !text-[14.5px] !font-normal": true,
												"!text-default": false,
												"!text-disabled": true,
											})}
										>
											{option.label}
										</Typography>
									}
								/>
							))}
						</RadioGroup>
					</FormControl>
				</Box>
				<Typography
					className={clsx({
						"!font-sans !text-[14.5px] !font-bold !normal-case !px-3": true,
						"!text-default": notificationType,
						"!text-neutral-gray": !notificationType,
					})}
				>
					Effective Date
				</Typography>
				{status === "pending" ? (
					<Box p="0 0.5rem">
						{Array.from({ length: 3 }).map((_, i) => (
							<Skeleton key={i} width="100%" height={20} />
						))}
					</Box>
				) : (
					cycleDatesOptions({
						notificationType: Boolean(notificationType),
						cycles,
						dateSelected,
						handleDateClick,
					})
				)}
			</Menu>
		</>
	);
}
AssignPayPlanButton.propTypes = {
	data: PropTypes.object,
	selected: PropTypes.object,
	selectedDate: PropTypes.string,
	hireDate: PropTypes.string,
	disabled: PropTypes.bool,
	updateUpcomingPayPlan: PropTypes.bool,
	onDateChanged: PropTypes.func,
	updateSelectedPlan: PropTypes.func,
};
